import { EWSS_GRADES, wordMatchesGroupementMode } from "../constants/registerFilters";
import { wordMatchesConsecutiveFailsFilter } from "./consecutiveGameFails";
import { wordMatchesPotionsUsedFilter } from "./trainingPotionAidFilter";
import { wordMatchesPlayedCountRange } from "./playedCountRangeFilter";

function getWordPaquetNumber(word) {
  const p = word?.paquet;
  const n =
    typeof p === "number"
      ? p
      : typeof p === "string" && p.trim() !== ""
        ? parseInt(p, 10)
        : NaN;
  if (Number.isFinite(n) && n >= 1 && n <= 5) return n;
  return null;
}

function parseDateForSort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const m = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return 0;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  return new Date(yyyy, mm - 1, dd).getTime();
}

function parseTimeForSort(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length < 2) return 0;
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

function getLastGameEntry(stats) {
  const last5 = stats?.last5Games;
  if (!Array.isArray(last5) || !last5.length) return null;
  return [...last5].sort((a, b) => {
    const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
    if (dateDiff !== 0) return dateDiff;
    return parseTimeForSort(b.time) - parseTimeForSort(a.time);
  })[0];
}

function getWordEwssGrade(word) {
  const retention = word?.stats?.retention;
  if (retention?.insufficientData || retention?.grade == null) return null;
  return retention.grade;
}

function isGradeAtOrBelow(wordGrade, thresholdGrade) {
  if (!wordGrade || !thresholdGrade) return false;
  const wordIndex = EWSS_GRADES.indexOf(wordGrade);
  const thresholdIndex = EWSS_GRADES.indexOf(thresholdGrade);
  if (wordIndex === -1 || thresholdIndex === -1) return false;
  return wordIndex >= thresholdIndex;
}

function isGradeAtOrAbove(wordGrade, thresholdGrade) {
  if (!wordGrade || !thresholdGrade) return false;
  const wordIndex = EWSS_GRADES.indexOf(wordGrade);
  const thresholdIndex = EWSS_GRADES.indexOf(thresholdGrade);
  if (wordIndex === -1 || thresholdIndex === -1) return false;
  return wordIndex <= thresholdIndex;
}

function getWordGameCount(word) {
  const count = word?.stats?.gameCount;
  return typeof count === "number" && count >= 0 ? count : 0;
}

function isNeverPlayedWord(word) {
  return getWordGameCount(word) === 0;
}

function wordHasEwssScore(word) {
  const retention = word?.stats?.retention;
  return Boolean(retention && !retention.insufficientData && retention.score != null);
}

function matchesNoEwssGameCountFilter(word, filterValue) {
  if (!filterValue) return false;
  if (wordHasEwssScore(word)) return false;

  const gameCount = getWordGameCount(word);
  if (filterValue === "once") return gameCount === 1;
  if (filterValue === "twice") return gameCount === 2;
  if (filterValue === "onceOrTwice") return gameCount === 1 || gameCount === 2;
  return false;
}

function matchesEwssFilters(word, setup) {
  if (setup.neverPlayed || setup.withoutEwssScore) {
    const checks = [];
    if (setup.neverPlayed) checks.push(isNeverPlayedWord(word));
    if (setup.withoutEwssScore) {
      checks.push(
        matchesNoEwssGameCountFilter(
          word,
          setup.noEwssGameCount || "onceOrTwice"
        )
      );
    }
    return checks.some(Boolean);
  }

  if (setup.exactGrade) {
    const grade = getWordEwssGrade(word);
    if (grade !== setup.exactGrade) return false;
  }

  if (setup.belowGrade) {
    const grade = getWordEwssGrade(word);
    if (!grade || !isGradeAtOrBelow(grade, setup.belowGrade)) return false;
  }

  if (setup.aboveGrade) {
    const grade = getWordEwssGrade(word);
    if (!grade || !isGradeAtOrAbove(grade, setup.aboveGrade)) return false;
  }

  return true;
}

function extractCaptureYear(word) {
  const raw = word?.date;
  if (!raw) return null;
  const match = String(raw).trim().match(/(\d{4})$/);
  if (!match) return null;
  const year = Number(match[1]);
  return Number.isFinite(year) ? year : null;
}

function buildNoyauContext(packsSummary, words) {
  const config = packsSummary?.config || {};
  const fromConfig = Array.isArray(config.noyauActif) ? config.noyauActif : [];
  const all = new Set(
    fromConfig.length
      ? fromConfig
      : (words || []).filter((w) => w?.noyauActif === true).map((w) => w.mot)
  );
  return { all };
}

function matchesCategory(word, setup, noyauContext) {
  const { categories } = setup;
  const active = Object.values(categories).some(Boolean);
  if (!active) return true;

  const mot = word.mot;
  const paquetNum = getWordPaquetNumber(word);
  const checks = [];

  if (categories.noyau) {
    checks.push(word?.noyauActif === true || noyauContext.all.has(mot));
  }
  if (categories.favoris) {
    checks.push(word?.categorie?.coeur === 1);
  }
  if (categories.prioritaires) {
    checks.push(word?.categorie?.top20 === 1);
  }

  if (categories.paquet1) checks.push(paquetNum === 1);
  if (categories.paquet2) checks.push(paquetNum === 2);
  if (categories.paquet3) checks.push(paquetNum === 3);
  if (categories.paquet4) checks.push(paquetNum === 4);
  if (categories.horsPaquet) {
    checks.push(
      paquetNum === 5 ||
        (paquetNum == null &&
          !noyauContext.all.has(mot) &&
          word?.noyauActif !== true)
    );
  }

  return checks.some(Boolean);
}

function wordMatchesIllustrationFilter(word, illustration, illustrationMots) {
  if (illustration !== "only" && illustration !== "none") return true;
  const hasIllustration =
    illustrationMots instanceof Set
      ? illustrationMots.has(word.mot)
      : Array.isArray(illustrationMots)
        ? illustrationMots.includes(word.mot)
        : false;
  return illustration === "only" ? hasIllustration : !hasIllustration;
}

export function filterTrainingWords(words, setup, packsSummary, illustrationMots = null) {
  if (!Array.isArray(words) || !words.length) return [];

  const noyauContext = buildNoyauContext(packsSummary, words);

  return words.filter((word) => {
    if (!word?.mot) return false;

    if (!matchesEwssFilters(word, setup)) return false;

    if (setup.lastAppearanceFailed) {
      const last = getLastGameEntry(word.stats);
      if (last?.result !== "fail") return false;
    }

    if (
      !wordMatchesConsecutiveFailsFilter(
        word,
        setup.consecutiveFails,
        setup.consecutiveFailsCount
      )
    ) {
      return false;
    }

    if (
      !wordMatchesPotionsUsedFilter(word, setup.potionsUsed, setup.potionsUsedFilter)
    ) {
      return false;
    }

    if (!wordMatchesPlayedCountRange(word, setup.playedCountMin, setup.playedCountMax)) {
      return false;
    }

    if (!wordMatchesIllustrationFilter(word, setup.illustration, illustrationMots)) {
      return false;
    }

    if (!wordMatchesGroupementMode(word, setup.groupement, setup.groupementId)) {
      return false;
    }

    if (!matchesCategory(word, setup, noyauContext)) return false;

    if (setup.livre && (word.livre || "").trim() !== setup.livre) return false;
    if (setup.auteur && (word.auteur || "").trim() !== setup.auteur) return false;

    if (setup.captureYear) {
      const year = extractCaptureYear(word);
      if (year == null || String(year) !== setup.captureYear) return false;
    }

    return true;
  });
}

export function shuffleTrainingWords(words) {
  const arr = [...words];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
